import { BackstageOverrides } from '@backstage/core-components';
import { alpha, Theme } from '@mui/material/styles';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../tokens';

export const styledBackstageTabbedCard = (
  theme: Theme,
): BackstageOverrides['BackstageTabbedCard'] => {
  return {
    root: {
      backgroundColor: alpha(getSurfaceColor(theme, 'raised'), 0.9),
      border: `1px solid ${getSurfaceBorder(theme)}`,
      borderRadius: radii.lg,
      boxSizing: 'border-box',
      margin: theme.spacing(0, 3, 2),
      minHeight: theme.spacing(5.5),
      overflow: 'hidden',
      padding: theme.spacing(0.5),
      width: 'fit-content',
      '& [class*="MuiTabs-flexContainer"]': {
        alignItems: 'center',
        borderBottom: 0,
        gap: theme.spacing(0.5),
        padding: 0,
      },
      '& [class*="MuiTabs-scroller"]': {
        minHeight: theme.spacing(4.5),
      },
      '& [class*="MuiTabs-indicator"]': {
        display: 'none',
      },
      '& + [class*="MuiDivider-root"]': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(0, 2, 2),
        maxWidth: `calc(100% - ${theme.spacing(4)})`,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    indicator: {
      display: 'none',
    },
  };
};

export const styledBackstageTabbedCardBoldHeader = (
  theme: Theme,
): BackstageOverrides['BackstageTabbedCardBoldHeader'] => ({
  root: {
    boxSizing: 'border-box',
    display: 'block',
    padding: theme.spacing(3, 3, 1.5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 1.25),
    },
  },
  title: {
    color: getThemeTokens(theme).textPrimary,
    fontSize: theme.typography.h5.fontSize,
    fontWeight: 700,
    lineHeight: theme.typography.h5.lineHeight,
  },
  subheader: {
    color: getThemeTokens(theme).textMuted,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    paddingTop: theme.spacing(0.75),
  },
});

export const styledBackstageCardTab = (
  theme: Theme,
): BackstageOverrides['BackstageCardTab'] => {
  const tokens = getThemeTokens(theme);

  return {
    root: {
      borderRadius: radii.md,
      color: tokens.textSecondary,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 700,
      lineHeight: theme.typography.body2.lineHeight,
      margin: 0,
      minHeight: theme.spacing(4.5),
      minWidth: 0,
      padding: theme.spacing(0.75, 1.5),
      textTransform: 'none',
      transition: 'color 150ms ease, background-color 150ms ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: tokens.textPrimary,
        opacity: 1,
      },
    },
    selected: {
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
      color: tokens.textPrimary,
      fontWeight: 700,
    },
  };
};
